/**
 * Settings store — manages API keys, config, and setup state.
 */

import { create } from "zustand";
import type { SettingsResponse, SettingValue } from "@/api/types";
import { apiFetch } from "@/api/client";

interface SettingsState {
  settings: Record<string, SettingValue>;
  setupComplete: boolean;
  /** Hosted cloud deployment (vs desktop app) — drives platform-specific UI. */
  cloudMode: boolean;
  /** Whether this user may edit SHARED (global) settings — always true on
   *  desktop, ADMIN-only on cloud (backend enforces; UI disables the fields). */
  canEditGlobal: boolean;
  /** True only during the initial load (gates App.tsx splash screen). */
  isLoading: boolean;
  /** True during any settings fetch (used by SettingsPage). */
  isFetching: boolean;

  /** Load settings from backend */
  loadSettings: () => Promise<void>;

  /** Update one or more settings */
  updateSettings: (updates: Record<string, string>) => Promise<void>;

  /**
   * Remove a stored secret entirely (clears the DB row, not just blanks it).
   *
   * `scope` only matters for a credential an admin can hold in both scopes
   * (`claude_token`). The default clears the caller's own; `"shared"` clears
   * the organisation's and needs an admin — see ADR-0014 amendment 6, where a
   * silent fall-through to the shared row took Claude away company-wide.
   * Resolves with what the server actually removed.
   */
  deleteSetting: (key: string, scope?: "personal" | "shared") => Promise<{ deleted: boolean; scope: string }>;

  /** Verify a specific secret. Pass context for unsaved form values (e.g. during onboarding). */
  verifySetting: (key: string, value: string, context?: Record<string, string>) => Promise<{ verified: boolean; message: string }>;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: {},
  setupComplete: false,
  cloudMode: false,
  canEditGlobal: true,
  isLoading: true,
  isFetching: false,

  loadSettings: async () => {
    const alreadyLoaded = !get().isLoading;
    // Only set isLoading on the very first load (gates the App.tsx splash).
    // Subsequent reloads (e.g. from SettingsPage) use isFetching instead.
    set(alreadyLoaded ? { isFetching: true } : { isLoading: true, isFetching: true });
    try {
      const res = await apiFetch<SettingsResponse>("/settings");
      set({ settings: res.settings, setupComplete: res.setup_complete, cloudMode: res.cloud_mode ?? false, canEditGlobal: res.can_edit_global ?? true, isLoading: false, isFetching: false });
    } catch (e) {
      console.error("Failed to load settings:", e);
      set({ isLoading: false, isFetching: false });
    }
  },

  updateSettings: async (updates: Record<string, string>) => {
    await apiFetch("/settings", {
      method: "PUT",
      body: JSON.stringify({ settings: updates }),
    });
    // Reload to get masked values
    const res = await apiFetch<SettingsResponse>("/settings");
    set({ settings: res.settings, setupComplete: res.setup_complete });
  },

  deleteSetting: async (key: string, scope?: "personal" | "shared") => {
    const result = await apiFetch<{ deleted: boolean; scope: string }>(
      `/settings/${key}${scope ? `?scope=${scope}` : ""}`,
      { method: "DELETE" },
    );
    // Reload so the removed key drops back to `set: false`.
    const res = await apiFetch<SettingsResponse>("/settings");
    set({ settings: res.settings, setupComplete: res.setup_complete });
    return result;
  },

  verifySetting: async (key: string, value: string, context?: Record<string, string>) => {
    return apiFetch<{ verified: boolean; message: string }>("/settings/verify", {
      method: "POST",
      body: JSON.stringify({ key, value, ...(context ? { context } : {}) }),
    });
  },
}));
